/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import {
	Chain,
	Environment,
	ProductAvailabilityCollection,
	ProductCatalogInput,
	ProductPromotionCollection,
	ProductVO
} from '@shared/data-access/interfaces';
import { OrchestratedProductAttributes } from '@shared/favorites/data-access/interfaces';

@Injectable({
	providedIn: 'root'
})
export class ProductCatalogService {
	constructor(
		private readonly http: HttpClient,
		private readonly environment: Environment,
		private readonly chain: Chain,
		@Inject(PLATFORM_ID) protected platformId: string
	) {}

	/**
	 * @description
	 * Get list of products from the product catalog api
	 * @param queryParams params for the call, see ProductCatalogInput for reference
	 */
	getListOfProducts$(queryParams: ProductCatalogInput) {
		// Standard HTTP Headers
		const httpHeaders = new HttpHeaders({
			['Content-Type']: 'application/json',
			channel: this.chain.chainIdentifierAbbr
		});

		// HTTP Options
		const httpOptions: any = {
			headers: httpHeaders
		};

		const params = encodeURIComponent(JSON.stringify(queryParams));
		const url = this.environment.api.elastic.searchTermUrl;

		// Make the HTTP Request
		return this.http
			.get(`${url}/v2/search?searchVO=${params}`, httpOptions)
			.pipe(catchError((error) => throwError(error)));
	}

	/**
	 * @description
	 * Get availability information for each ecode in the product list
	 * @param productList List of products from product catalog data
	 * @param storeZipCode Zipcode of the currently selected store
	 * @param selectedStoreId Id of the currently selected store
	 * @param boplExperienceEnabled Feature flag for bopl
	 */
	getProductListAvailabilityData(
		productVOs: ProductVO[],
		boplExperienceEnabled: boolean,
		storeZipCode?: string,
		selectedStoreId?: string
	): Observable<ProductAvailabilityCollection | null> {
		if (!productVOs?.length) {
			return of(null);
		}

		const ecodeList: string[] = productVOs
			.filter((item) => item?.parentPartnumber)
			.map((item) => item.parentPartnumber);

		return this.getAvailabilityData(
			ecodeList,
			boplExperienceEnabled,
			storeZipCode,
			selectedStoreId
		);
	}

	getAvailabilityData(
		ecodeList: string[],
		boplExperienceEnabled: boolean,
		storeZipCode?: string,
		selectedStoreId?: string
	): Observable<ProductAvailabilityCollection | null> {
		// Checking again in case none of the productVOs in the argument had a parentPartNumber
		if (!ecodeList.length) {
			return of(null);
		}

		const inventoryRequest = `${this.environment.api.elastic.searchTermUrl}v1/inventory/messages`;

		const params = {
			ecodeList,
			...(selectedStoreId && { selectedStore: selectedStoreId }),
			...(storeZipCode && { zipCode: storeZipCode }),
			...(boplExperienceEnabled && { bopl: 'true' })
		};

		const httpParams = new HttpParams().appendAll(params);

		const httpOptions = {
			params: httpParams
		};

		// Make the HTTP Request
		return this.http.get<ProductAvailabilityCollection>(inventoryRequest, httpOptions).pipe(
			timeout(5000),
			catchError((error) => throwError(() => error))
		);
	}

	getFavoritesAvailabilityData(
		favPartNums: string[],
		boplExperienceEnabled: boolean,
		storeZipCode?: string,
		selectedStoreId?: string
	): Observable<ProductAvailabilityCollection | null> {
		if (!favPartNums?.length) {
			return of(null);
		}

		const inventoryRequest = `${this.environment.api.elastic.searchTermUrl}v1/inventory/messages`;
		const params = {
			ecodeList: favPartNums,
			...(selectedStoreId && { selectedStore: selectedStoreId }),
			...(storeZipCode && { zipCode: storeZipCode }),
			...(boplExperienceEnabled && { bopl: 'true' })
		};

		const httpParams = new HttpParams().appendAll(params);
		const httpOptions = {
			params: httpParams
		};

		// Make the HTTP Request
		return this.http
			.get<ProductAvailabilityCollection>(inventoryRequest, httpOptions)
			.pipe(catchError((error) => throwError(error)));
	}

	/**
	 * @description
	 * Get availability information for each ecode in the product list
	 * @param productList List of products from product catalog data
	 * @param storeZipCode Zipcode of the currently selected store
	 * @param selectedStoreId Id of the currently selected store
	 * @param boplExperienceEnabled Feature flag for bopl
	 */
	getProductPromoList(
		productVOs: ProductVO[] | OrchestratedProductAttributes[]
	): Observable<ProductPromotionCollection> {
		const catentryIds = [
			...new Set(
				productVOs.map((item) => {
					return item.parentCatentryId || item.catentryId;
				})
			)
		];

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Custom HTTP Headers
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
		httpHeaders = httpHeaders.set(
			CustomHttpHeaders.X_CENTRASITE_KEY,
			this.environment.api.esb.promotions.apiKey
		);
		httpHeaders = httpHeaders.set(
			CustomHttpHeaders.ENVIRONMENT,
			this.environment.api.esb.promotions.env
		);

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.api.esb.gatewayUrlProxy.timeout}`
			);
		}

		// Set http parameters
		const httpParams = new HttpParams()
			.set('catentryIds', `[${catentryIds}]`)
			.set('previewTime', this.environment.api.esb.promotions.previewTime)
			.set('channelId', this.environment.api.esb.promotions.channelId);

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};

		// Make the HTTP Request
		return this.http
			.get(
				`${this.environment.api.esb.gatewayUrl}ws/WCS/v2/api/promotiondescription/${this.chain.storeId}`,
				httpOptions
			)
			.pipe(catchError((error) => throwError(error)));
	}
}
